import { Filters, Pagination } from '../types'
import moment from 'moment'
import { useConfigurationStore } from '../../store/configurations'

export const sleep = (ms = 0) => {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Validation */
export const validators = {
  email: (v: string) => {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return pattern.test(v) || 'Please enter a valid email address'
  },
  required: (v: any) => !!v || 'This field is required',
  /** Required validator that allows 0 (useful for numeric inputs). */
  requiredNumber: (v: any) => (v !== '' && v !== null && v !== undefined) || 'This field is required',
  /** Minimum value (inclusive). Use for numbers; empty string passes. */
  minValue: (min: number) => (v: any) => {
    if (v === '' || v === null || v === undefined) return true
    const n = Number(v)
    return (Number.isFinite(n) && n >= min) || `Must be at least ${min}`
  },
  /** Strictly greater than zero (required numeric). */
  greaterThanZero: (v: any) => {
    if (v === '' || v === null || v === undefined) return 'This field is required'
    const n = Number(v)
    return (Number.isFinite(n) && n > 0) || 'Amount must be greater than zero'
  },
}

/** Coerces a value to a finite number; returns undefined when empty/invalid. */
export const toFiniteNumberOrUndefined = (v: any): number | undefined => {
  if (v === '' || v === null || v === undefined) return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

/** Coerces a value to a finite number; returns null when empty/invalid. */
export const toFiniteNumberOrNull = (v: any): number | null => {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Finite number for API bodies that reject `null` when the key is sent (`sometimes` + `required`).
 * Empty or invalid input becomes 0 — use after `toFiniteNumberOrNull` in forms when clearing means “no default / zero”.
 */
export const toFiniteNumberOrZero = (v: any): number => toFiniteNumberOrUndefined(v) ?? 0

export const parseDate = (date: string, format: string = 'YYYY/MM/DD') => {
  return moment(date).format(format)
}

export const formatDate = (date: Date) => {
  return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
}

/**
 * Normalizes VaDateInput-style values to `YYYY-MM-DD` for APIs, or `null` when empty/invalid.
 * Accepts ISO date strings, parseable date strings, or `Date`.
 */
export function dateToIso(d: Date | string | null | undefined): string | null {
  if (d === null || d === undefined) return null
  if (typeof d === 'string') {
    const trimmed = d.trim()
    if (!trimmed) return null
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed
    const parsed = new Date(trimmed)
    if (!Number.isFinite(parsed.getTime())) return null
    const y = parsed.getFullYear()
    const m = String(parsed.getMonth() + 1).padStart(2, '0')
    const day = String(parsed.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  if (d instanceof Date) {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }
  return null
}

export const parseDateToTime = (date: Date | undefined) => {
  // Parse the string into a moment object
  const momentObj = moment(date)

  // Format the time as "HH:mm"
  return momentObj.format('HH:mm')
}

export const parseTimeToDate = (timeString: string) => {
  // Parse the string into a moment object
  const momentObj = moment(timeString, 'HH:mm')
  // Convert the moment object to a JavaScript Date object
  return momentObj.toDate()
}

/**
 * Returns true when the provided date is strictly after today (date-only comparison).
 * Accepts values from inputs like VaDateInput: string ("YYYY-MM-DD"), Date, number, etc.
 */
export const isAfterToday = (v: unknown): boolean => {
  if (!v) return false
  const candidate =
    typeof v === 'string'
      ? new Date(`${v}T00:00:00`)
      : typeof v === 'number'
        ? new Date(v)
        : v instanceof Date
          ? new Date(v)
          : new Date(String(v))
  if (!Number.isFinite(candidate.getTime())) return false

  candidate.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return candidate.getTime() > today.getTime()
}

/**
 * Returns true when the provided date is strictly before today (date-only comparison).
 * Accepts values from inputs like VaDateInput: string ("YYYY-MM-DD"), Date, number, etc.
 */
export const isBeforeToday = (v: unknown): boolean => {
  if (!v) return false
  const candidate =
    typeof v === 'string'
      ? new Date(`${v}T00:00:00`)
      : typeof v === 'number'
        ? new Date(v)
        : v instanceof Date
          ? new Date(v)
          : new Date(String(v))
  if (!Number.isFinite(candidate.getTime())) return false

  candidate.setHours(0, 0, 0, 0)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return candidate.getTime() < today.getTime()
}

// Builds a parameter string for the endpoint that has searching and pagination.
export const getRequestParamString = (filters?: Partial<Filters>, pagination?: Partial<Pagination>): string => {
  let paramString = ''

  // Handle legacy single filter (for backward compatibility)
  if (filters?.search && filters?.column) {
    paramString = `?search=${filters.search}&column=${filters.column}`
  }

  // Handle multi-filters. These sit beside a search chip when both are set.
  if (filters?.filters && filters.filters.length > 0) {
    filters.filters.forEach((filter, index) => {
      paramString += getParamString(paramString, `filters[${index}][column]=${filter.column}`)
      paramString += `&filters[${index}][search]=${filter.search}`

      if (filter.start_date) {
        paramString += `&filters[${index}][start_date]=${parseDate('' + filter.start_date)}`
      }

      if (filter.end_date) {
        paramString += `&filters[${index}][end_date]=${parseDate('' + filter.end_date)}`
      }

      if (filter.operator) {
        paramString += `&filters[${index}][operator]=${filter.operator}`
      }
    })
  }

  // Handle legacy date filters (for backward compatibility)
  if (filters?.start_date && !filters?.filters) {
    paramString += filters?.start_date
      ? getParamString(paramString, `start_date=${parseDate('' + filters.start_date)}`)
      : ''
  }

  if (filters?.end_date && !filters?.filters) {
    paramString +=
      paramString.length > 0 && filters?.end_date
        ? getParamString(paramString, `end_date=${parseDate('' + filters.end_date)}`)
        : ''
  }

  // Inventory health list filters (same params as GET /items/inventory-status-summary cards)
  if (filters?.stock_status) {
    paramString += getParamString(paramString, `stock_status=${encodeURIComponent(filters.stock_status)}`)
  }
  if (filters?.expiry_status) {
    paramString += getParamString(paramString, `expiry_status=${encodeURIComponent(filters.expiry_status)}`)
  }

  // Add pagination
  paramString += pagination?.page ? getParamString(paramString, `page=${pagination.page}`) : ''
  paramString += pagination?.perPage ? getParamString(paramString, `per_page=${pagination.perPage}`) : ''

  return paramString
}

// Cocatenates a supplied string to the original parameter string
const getParamString = (paramString: string, param: string): string => {
  if (paramString.length <= 0) {
    return (paramString = '?' + param)
  }

  return (paramString = '&' + param)
}

// Get cookie value. Laravel's XSRF-TOKEN is URL-encoded; a leftover or truncated
// value must not throw (decodeURIComponent / unescape) and freeze boot.
export const getCookie = (name: string) => {
  const escapedName = name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1')
  const re = new RegExp('(?:^|; )' + escapedName + '=([^;]*)')
  const value = re.exec(document.cookie)
  if (value == null) {
    return null
  }
  try {
    return decodeURIComponent(value[1])
  } catch {
    return value[1]
  }
}

export const findInArray = (arrayObj: Array<any>, identifierParam: string, valueParam: string | number) =>
  arrayObj.find((item) => item[identifierParam] === valueParam)

export const getNestedObjectValue = (obj: any, key: string) => {
  return key.split('.').reduce((o, k) => (o || {})[k], obj)
}

export const getSearchColumnKey = (column: any) => {
  return column.alternativeKey ? column.alternativeKey : column.key.replace(/\./g, '_')
}

export const setPagination = (pagination: Pagination) => {
  return {
    page: pagination.meta?.current_page ? pagination.meta?.current_page : pagination.page,
    perPage: pagination.meta?.per_page ? pagination.meta?.per_page : pagination.perPage,
    total: pagination.meta?.total ? pagination.meta?.total : pagination.total,
  }
}

export const printReceipt = (
  receiptElement: any,
  options: {
    printerType?: 'pos' | 'standard'
    paperWidth?: string
    showPreview?: boolean
  } = {},
) => {
  const { printerType = 'pos', paperWidth = '80mm', showPreview = false } = options

  const printWindow = window.open('', '_blank')

  if (!printWindow) {
    console.error('Failed to open print window')
    return
  }

  const isPOS = printerType === 'pos'

  printWindow.document.write(`
    <html>
    <head>
      <title>Print Receipt</title>
      <style>
        @page { 
          size: ${paperWidth} auto; 
          margin: 0; 
        }
        
        body { 
          margin: 0; 
          font-family: ${isPOS ? 'Courier New, monospace' : 'Arial, sans-serif'};
          font-size: ${isPOS ? '10px' : '12px'};
          line-height: 1.5;
          background: white;
          color: black;
        }
        
        .receipt-container {
          width: ${paperWidth};
          max-width: ${paperWidth};
          margin: 0 auto;
          padding: ${isPOS ? '5px' : '10px'};
        }
        
        /* Hide elements that shouldn't be printed */
        @media print {
          .no-print { display: none !important; }
          body { -webkit-print-color-adjust: exact; }
        }
        
        /* POS-specific styles */
        ${
          isPOS
            ? `
          .receipt-template {
            font-family: 'Courier New', monospace;
            font-size: 10px;
            line-height: 1.5;
          }
          
          .business-name { font-size: 12px; font-weight: bold; }
          .receipt-title { font-size: 11px; font-weight: bold; }
          .items-header, .item-row { font-size: 9px; }
          .grand-total { font-size: 11px; font-weight: bold; }
          .footer-text { font-size: 8px; }
        `
            : ''
        }
      </style>
    </head>
    <body>
      <div class="receipt-container">
        ${receiptElement}
      </div>
      ${
        showPreview
          ? `
        <div class="no-print" style="position: fixed; top: 10px; right: 10px; z-index: 1000;">
          <button onclick="window.print()" style="padding: 10px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer;">
            Print Receipt
          </button>
          <button onclick="window.close()" style="padding: 10px; background: #6c757d; color: white; border: none; border-radius: 5px; cursor: pointer; margin-left: 10px;">
            Close
          </button>
        </div>
      `
          : ''
      }
    </body>
    </html>
  `)

  printWindow.document.close()

  if (!showPreview) {
    // Auto-print for POS printers
    printWindow.focus()
    printWindow.print()

    // Close window after printing (with a small delay to ensure printing starts)
    setTimeout(() => {
      printWindow.close()
    }, 1000)
  }
}

export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj))
}

export { toCents } from '../../utils/cents'

/** Display name for a sale/invoice customer; walk-ins have no registered client. */
export const getCustomerDisplayName = (
  record?: {
    client?: { name?: string | null } | null
    customer_name?: string | null
    customer_type_label?: string | null
  } | null,
): string => {
  return record?.client?.name || record?.customer_name || record?.customer_type_label || 'Walk-in'
}

export const formatCurrency = (
  amount: number | null | undefined,
  options: {
    locale?: string
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  } = {},
) => {
  const { locale = 'en-US', minimumFractionDigits = 2, maximumFractionDigits = 2 } = options

  if (amount === null || amount === undefined) {
    return '0.00'
  }

  return amount.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  })
}

/** Default fraction bounds for monetary amounts shown in expense-style tables/lists. */
const AMOUNT_DISPLAY_FRACTIONS = { minimumFractionDigits: 2, maximumFractionDigits: 4 } as const

/**
 * Formats monetary amounts often returned as decimals in API payloads (`string | number`).
 * Invalid coercions fall back to `String(value)`; `undefined` / `null` render as `emptyPlaceholder`.
 */
export function formatAmountDisplay(value: unknown, emptyPlaceholder = '—'): string {
  if (value === undefined || value === null) return emptyPlaceholder
  const n = typeof value === 'string' ? Number(value) : typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? formatCurrency(n, AMOUNT_DISPLAY_FRACTIONS) : String(value)
}

export const formatCurrencyWithCode = (
  amount: number | null | undefined,
  options: {
    locale?: string
    currency?: string
    minimumFractionDigits?: number
    maximumFractionDigits?: number
  } = {},
) => {
  const {
    locale = 'en-US',
    currency = useConfigurationStore().currency,
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options

  if (amount === null || amount === undefined) {
    return '0.00'
  }

  return amount.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
  })
}
